import React from 'react';
import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo } from 'react';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { MetricLabel } from '../../common/MetricLabel';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';

interface ImpactMetric {
  label: string;
  value: string;
  isPositive: boolean;
  isPrimary?: boolean;
  currentValue?: string;
  proposedValue?: string;
  description?: string | React.ReactNode;
}

const ImpactMetricCard: React.FC<{ metric: ImpactMetric }> = ({ metric }) => {
  return (
    <Card
      className="h-full flex flex-column"
      pt={{
        body: { className: 'p-3 flex-1' },
        content: { className: 'p-0 m-0 flex-1' },
      }}
    >
      <MetricLabel label={metric.label} description={metric.description} />

      <div className="flex-1 flex flex-column">
        {metric.currentValue && metric.proposedValue ? (
          <div className="flex flex-column gap-2">
            <div className="flex justify-content-between align-items-center">
              <span
                className="text-sm"
                style={{ color: 'var(--bq-text-secondary)' }}
              >
                Current
              </span>
              <span
                className="font-semibold"
                style={{ color: 'var(--bq-text-primary)' }}
              >
                {metric.currentValue}
              </span>
            </div>
            <div className="flex justify-content-between align-items-center">
              <span
                className="text-sm"
                style={{ color: 'var(--bq-text-secondary)' }}
              >
                Proposed
              </span>
              <span
                className="font-semibold"
                style={{ color: 'var(--bq-text-primary)' }}
              >
                {metric.proposedValue}
              </span>
            </div>
            <div
              className="border-top-1 pt-2 mt-1"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="flex justify-content-between align-items-center">
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'var(--bq-text-secondary)' }}
                >
                  Change
                </span>
                <span
                  className="font-bold"
                  style={{
                    color: metric.isPositive
                      ? 'var(--bq-color-sucess)'
                      : 'var(--bq-color-error)',
                  }}
                >
                  {metric.value}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="text-lg font-semibold"
            style={{
              color: metric.isPositive
                ? 'var(--bq-color-sucess)'
                : 'var(--bq-color-error)',
            }}
          >
            {metric.value}
          </div>
        )}
      </div>
    </Card>
  );
};

export type ExpectedImpactWidgetProps = {
  data: ImpactMetric[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
};

export const ExpectedImpactWidget: React.FC<ExpectedImpactWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Expected Impact',
  snapshotId,
}) => {
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: ExpectedImpactWidget,

    title: title || 'Expected Impact',
    defaultWidth: 800,
    defaultHeight: 600,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
    },
  });

  const noData = useMemo(() => {
    return !data.length;
  }, [data]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No data found."
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
      />

      {/* Full Height Content with Scrollable Cards */}
      <div className="flex-1 px-3 pt-3 overflow-y-auto">
        <div className="grid" style={{ minHeight: '100%' }}>
          {data.map((metric, index) => (
            <div key={`${metric.label}-${index}`} className="col-12 md:col-6">
              <ImpactMetricCard metric={metric} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
